// src/pages/AccountVerify.tsx
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { authFetch } from "../lib/auth";
import BankSelect from "../components/BankSelect";

type StartResponse = {
  ok: boolean;
  requestId?: string;
  error?: string;
};

type ConfirmResponse = {
  ok: boolean;
  error?: string;
};

export default function AccountVerify() {
  const nav = useNavigate();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const next = search.get("next") || "/onboarding";

  const [bankCode, setBankCode] = useState("004"); // 기본값: 국민은행
  const [accountNo, setAccountNo] = useState("");
  const [accountName, setAccountName] = useState("");
  const [requestId, setRequestId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"start" | "confirm">("start");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // 1) 1원 송금 시작
  async function handleStart(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!bankCode || !accountNo || !accountName) {
      setError("은행, 계좌번호, 예금주명을 입력해 주세요.");
      return;
    }

    setLoading(true);
    try {
      const res = await authFetch("/api/onewon/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bankCode, accountNo, accountName }),
      });

      const data: StartResponse = await res.json().catch(() => ({} as any));

      if (!res.ok || !data.ok || !data.requestId) {
        console.error("[1원:start] fail", data);
        setError(data.error || "1원 송금 요청에 실패했습니다.");
        return;
      }

      setRequestId(data.requestId);
      setStep("confirm");
      setCode("");
    } catch (err) {
      console.error(err);
      setError("1원 송금 요청 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }

  // 2) 인증문자 확인
  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!requestId) {
      setError("요청 번호가 없습니다. 처음부터 다시 진행해 주세요.");
      return;
    }
    if (!code.trim()) {
      setError("송금자명을 입력해 주세요.");
      return;
    }

    setLoading(true);
    try {
      const res = await authFetch("/api/onewon/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requestId, verifyValue: code.trim() }),
      });

      const data: ConfirmResponse = await res.json().catch(() => ({} as any));

      if (!res.ok) {
        console.error("[1원:confirm] HTTP fail", data);
        setError(data.error || "인증문자 확인에 실패했습니다.");
        return;
      }

      if (!data.ok) {
        setError(data.error || "인증문자가 일치하지 않습니다.");
        return;
      }

      // 성공 시: "완료되었습니다." 보여주고 홈(또는 next)으로 이동
      setMessage("완료되었습니다.");
      const dest = next === "/onboarding" ? "/" : next;
      setTimeout(() => {
        nav(dest, { replace: true });
      }, 1500);
    } catch (err) {
      console.error(err);
      setError("인증문자 확인 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }

  const showConfirmForm = step === "confirm";

  const handleBack = () => {
    const dest = next || "/";
    nav(dest, { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto flex max-w-md flex-col px-4 py-10">
        <h1 className="mb-2 text-center text-2xl font-bold text-slate-900">
          1원 계좌 인증
        </h1>
        <p className="mb-6 text-center text-sm text-slate-600">
          고객님의 계좌로 1원을 송금하고, 송금자명을 입력하여 계좌 소유 여부를
          확인합니다.
        </p>

        {error && (
          <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}

        {message && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            {message}
          </div>
        )}

        {!showConfirmForm ? (
          // ====== STEP 1: 송금 요청 ======
          <form
            onSubmit={handleStart}
            className="space-y-4 rounded-2xl bg-white p-5 shadow-sm"
          >
            <BankSelect
              label="은행"
              value={bankCode}
              onChange={setBankCode}
            />

            <div>
              <label className="mb-1 block text-sm text-slate-700">
                계좌번호
              </label>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                value={accountNo}
                onChange={(e) => setAccountNo(e.target.value)}
                placeholder="숫자만 입력"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm text-slate-700">
                예금주명
              </label>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-2 w-full rounded-xl bg-brand-500 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? "요청 중..." : "1원 인증 시작하기"}
            </button>
          </form>
        ) : (
          // ====== STEP 2: 인증문자 입력 ======
          <form
            onSubmit={handleConfirm}
            className="space-y-4 rounded-2xl bg-white p-5 shadow-sm"
          >
            <p className="text-xs text-slate-500">
              거래내역에서{" "}
              <span className="font-semibold text-slate-700">"카이로스"</span>{" "}
              처럼 표시된 입금자명을 확인한 뒤, 그 입금자명을 입력해 주세요.
            </p>

            <div>
              <label className="mb-1 block text-sm text-slate-700">
                입금자명
              </label>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="예: 카이로스"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-2 w-full rounded-xl bg-brand-500 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? "확인 중..." : "인증 완료하기"}
            </button>
          </form>
        )}

        {/* ✅ 항상 보이는 이전 단계로 돌아가기 버튼 */}
        <button
          type="button"
          onClick={handleBack}
          className="mt-4 w-full rounded-xl border border-slate-300 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          이전 단계로 돌아가기
        </button>
      </div>
    </div>
  );
}
